// SAB-847: NFL's draft-board position palette, extracted from NFLDraftPage.
//
// Moved out of the component file for two reasons: DraftBoard's NFL call site imports
// getBoardCellColor as its `colorFor` resolver, and exporting a non-component from a
// component file trips react-refresh/only-export-components. Living here also lets it be
// unit-tested without importing the page, and with it the supabase client.
//
// This is the ONE NFL palette. DraftBoard's built-in `positionColors` is MLB's and is
// deliberately not extended -- a second NFL palette there would be free to drift from
// nfl_slot_eligibility, the duplication SAB-739 removed.

export const DRAFT_BOARD_POS_COLOR: Record<string, { text: string; border: string; bgFaint: string }> = {
  QB:    { text: 'text-[hsl(0_84%_60%)]',    border: 'border-[hsl(0_84%_60%)]',    bgFaint: 'bg-[hsl(0_84%_60%/0.12)]' },
  RB:    { text: 'text-[hsl(220_90%_56%)]',  border: 'border-[hsl(220_90%_56%)]',  bgFaint: 'bg-[hsl(220_90%_56%/0.12)]' },
  WR:    { text: 'text-[hsl(160_84%_45%)]',  border: 'border-[hsl(160_84%_45%)]',  bgFaint: 'bg-[hsl(160_84%_45%/0.12)]' },
  TE:    { text: 'text-[hsl(45_93%_47%)]',   border: 'border-[hsl(45_93%_47%)]',   bgFaint: 'bg-[hsl(45_93%_47%/0.12)]' },
  K:     { text: 'text-[hsl(280_85%_60%)]',  border: 'border-[hsl(280_85%_60%)]',  bgFaint: 'bg-[hsl(280_85%_60%/0.12)]' },
  P:     { text: 'text-[hsl(174_58%_45%)]',  border: 'border-[hsl(174_58%_45%)]',  bgFaint: 'bg-[hsl(174_58%_45%/0.12)]' },
  // Spec lumps DST/COACH/IDP under one muted gray.
  DST:   { text: 'text-[hsl(215_16%_55%)]',  border: 'border-[hsl(215_16%_55%)]',  bgFaint: 'bg-[hsl(215_16%_55%/0.12)]' },
  COACH: { text: 'text-[hsl(215_16%_55%)]',  border: 'border-[hsl(215_16%_55%)]',  bgFaint: 'bg-[hsl(215_16%_55%/0.12)]' },
};
// SAB-812: the IDP_POSITIONS constant that lived here is GONE. It was a hand-maintained
// copy of `nfl_slot_eligibility WHERE slot_base='IDP'` that had already drifted once
// (SAB-806 -- it omitted SAF/OLB/NT/MLB, 332 active players, while listing S/SS which match
// nothing), and correcting a copy leaves it a copy. The draft pool's IDP tab now takes the
// set from `useLeagueRosterability`, which reads the table.
//
// Nothing was lost here in colour terms: both arms of the old ternary returned the DST
// grey, so the set never affected the palette at all -- it only ever mattered to the tab.
// Every unmapped position (IDP or otherwise) still folds onto that grey.
export function getDraftBoardPosColor(pos: string) {
  return DRAFT_BOARD_POS_COLOR[pos] ?? DRAFT_BOARD_POS_COLOR.DST;
}
/**
 * SAB-847: NFL's palette as the single class string DraftBoard's cell wants.
 *
 * Exported so a test can assert the property that matters: this NEVER returns undefined.
 * DraftBoard composes its cell class as `resolveColor(pos) + " border"`, so a resolver
 * that returned undefined for an unmapped position would produce the literal className
 * "undefined border" — no colour, no error, no way to notice. getDraftBoardPosColor
 * folds every unknown position onto the DST grey, which is why NFL passes this rather
 * than letting the board's built-in map miss.
 *
 * 15 of the 20 positions that appear in real prod picks are absent from that built-in
 * map (DST, COACH, P and every IDP variant), covering 445 of 961 drafted picks.
 */
export function getBoardCellColor(pos: string): string {
  const c = getDraftBoardPosColor(pos);
  return `${c.bgFaint} ${c.text} ${c.border}`;
}
